import { useCallback, useMemo, useRef, useState } from 'react';

import type { GetDraftLessonActivitiesQuery } from '../../lesson-builder.graphql.generated';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import {
  ActivityBuilderDualNav,
  ActivityPresentNextControl,
  ActivityPresentPrevControl,
} from 'approot/activity-builder/present/activity-present-nav-controls';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { ImageThumbnail } from 'approot/shared/images/images';
import { scrollSlideContainerBy } from 'approot/shared/logic/horizontal-scroll-fallback';
import { get3To6Definition, getKTo2Definition } from './vocabulary.utils';
import { Definition } from './vocabulary.constants';
import VocabularyViewLexical from './vocabulary-view-lexical';
import * as Styled from './vocabulary-view-modal.style';
import {
  ASSESSMENT,
  ActivitiesContentType,
  LANGUAGE_CODE_EN_US,
  LANGUAGE_CODE_ES_US,
  LESSON,
  LESSON_ACTIVITIES,
  LanguageCodes,
} from 'global/constants';
import VocabularyViewTabs, {
  VOCABULARY_TABS_ID,
} from 'approot/activity-builder/components/vocabulary/vocabulary-view-modal-tabs';
import { getVocabularyViewTabOptions } from 'approot/activity-builder/components/vocabulary/vocabulary-view-modal.utils';
import { usePresentTabNavigation } from 'approot/activity-builder/present/activity-builder-present.hooks';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import {
  EVENT_ACTION_SWITCH_TAB,
  EVENT_CATEGORY_ACTIVITY_COMPONENT,
} from 'approot/shared/data-tracker/data-events.constants';
import { TabOption } from 'approot/shared/tabs/tabs.constants';
import ActivitySheet from '../shared/sheet/activity-sheet';
import {
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../shared/sheet/activity-sheet.style';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  definition: Definition;
  years?: Pluck<
    GetDraftLessonActivitiesQuery,
    'getDraftLessonActivities.lesson.years'
  >[];
  contentId: number;
  sectionId: string;
  contentType: ActivitiesContentType;
  componentId: string;
  isStudent?: boolean;
  closeModal: () => void;
};

const VocabularyViewModal = ({
  definition,
  years,
  contentId,
  contentType,
  componentId,
  sectionId,
  isStudent,
  closeModal,
}: Props) => {
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [language, setLanguage] = useState<LanguageCodes>(LANGUAGE_CODE_EN_US);

  usePresentTabNavigation(VOCABULARY_TABS_ID);

  const tabOptions = useMemo(
    () => getVocabularyViewTabOptions({ definition }),
    [definition]
  );

  const isKTo2 = useMemo(() => years?.some(y => Number(y.name) <= 2), [years]);

  const description = useMemo(() => {
    if (isKTo2) {
      return getKTo2Definition(definition, language);
    }
    return get3To6Definition(definition, language);
  }, [isKTo2, definition, language]);

  const examples = useMemo(
    () =>
      definition.aiDefinitions?.examples.filter(
        item => item.language === language || item.language === null
      ) || [],
    [definition, language]
  );
  const languageTag = useMemo(
    () => (language === LANGUAGE_CODE_ES_US ? LANGUAGE_CODE_ES_US : undefined),
    [language]
  );
  const handleScroll = useCallback(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      const minPosition = Math.round(
        (carousel.scrollLeft / carousel.scrollWidth) * examples.length
      );
      setCurrentSlide(minPosition);
    }
  }, [examples]);

  const handlePrev = useCallback(() => {
    if (carouselRef.current) {
      const slide = carouselRef.current.querySelector(
        `#slide-${currentSlide - 1}`
      ) as HTMLElement;
      if (slide) {
        scrollSlideContainerBy(carouselRef.current, slide, 'left');
      }
    }
  }, [currentSlide]);

  const handleSelectLanguage = useCallback(
    (languageOption: TabOption) => {
      pushDataTrackerEvent(
        EVENT_CATEGORY_ACTIVITY_COMPONENT,
        EVENT_ACTION_SWITCH_TAB,
        {
          target_type: 'vocabulary',
          target_identifier: componentId,
          content_id: contentId,
          content_type: contentType === LESSON_ACTIVITIES ? LESSON : ASSESSMENT,
          section_id: sectionId,
          vocab_word: languageOption.label,
          language: languageOption.value,
        }
      );
      setLanguage(languageOption.value as LanguageCodes);
    },
    [componentId, contentId, contentType, sectionId]
  );

  const handleNext = useCallback(() => {
    if (carouselRef.current) {
      const slide = carouselRef.current.querySelector(
        `#slide-${currentSlide + 1}`
      ) as HTMLElement;
      if (slide) {
        scrollSlideContainerBy(carouselRef.current, slide, 'right');
      }
    }
  }, [currentSlide]);

  return (
    <ActivitySheet
      isVisible
      hasBackgroundColor
      title={`Vocabulary for: ${definition.word}`}
      onClose={closeModal}
      tabsContent={
        <VocabularyViewTabs
          language={language}
          onSelectLanguage={languageOption => {
            handleSelectLanguage(languageOption);
          }}
          tabOptions={tabOptions}
        />
      }
    >
      <ActivitySheetInnerContent>
        <ActivitySheetInnerMax id="vocabulary-content" tabIndex={-1}>
          <VocabularyViewLexical
            lexical={definition.lexical}
            description={description}
            languageTag={languageTag}
          />
          <ScreenReaderOnly as="h2" id="examples">
            Examples
          </ScreenReaderOnly>
          <Styled.VocabularyExamples lang={languageTag}>
            <Styled.VocabularyExampleList
              role="list"
              ref={carouselRef}
              aria-describedby="examples"
              onScroll={handleScroll}
            >
              {examples.map((item, index) => (
                <Styled.VocabularyExampleInfo
                  key={`${item.language}-${item.exampleId}`}
                  id={`slide-${index}`}
                  role="listitem"
                >
                  {item.image ? (
                    <Styled.VocabularyImage>
                      <ImageThumbnail
                        images={item.image?.responsive || []}
                        alt={item.image.alt || ''}
                      />
                    </Styled.VocabularyImage>
                  ) : (
                    <Styled.VocabularyEmptyImage>
                      <InlineSvg id="image" width={80} height={80} />
                    </Styled.VocabularyEmptyImage>
                  )}
                  <Styled.VocabularyExampleText>
                    {item.example}
                  </Styled.VocabularyExampleText>
                </Styled.VocabularyExampleInfo>
              ))}
            </Styled.VocabularyExampleList>
            {examples.length > 1 && (
              <>
                {isStudent ? (
                  <>
                    <ActivityPresentPrevControl
                      isStart={currentSlide === 0}
                      ariaLabel="example"
                      ariaNavigationLabel="Example pagination"
                      handlePrev={handlePrev}
                    />
                    <ActivityPresentNextControl
                      isEnd={currentSlide === examples.length - 1}
                      ariaLabel="example"
                      ariaNavigationLabel="Example pagination"
                      handleNext={handleNext}
                    />
                  </>
                ) : (
                  <ActivityBuilderDualNav
                    isEnd={currentSlide === examples.length - 1}
                    isStart={currentSlide === 0}
                    ariaLabel="example"
                    ariaNavigationLabel="Example pagination"
                    handleNext={handleNext}
                    handlePrevious={handlePrev}
                  />
                )}
              </>
            )}
          </Styled.VocabularyExamples>
          <ScreenReaderOnly role="region" aria-live="polite">
            {`Viewing ${currentSlide + 1} of ${examples.length} examples`}
          </ScreenReaderOnly>
          {examples.length > 1 && (
            <Styled.VocabularyDots aria-hidden>
              <div>
                {examples.map((s, i) => (
                  <Styled.VocabularyDot
                    key={i}
                    $isActive={i === currentSlide}
                  />
                ))}
              </div>
            </Styled.VocabularyDots>
          )}
        </ActivitySheetInnerMax>
      </ActivitySheetInnerContent>
    </ActivitySheet>
  );
};

export default VocabularyViewModal;
